import GuidanceReferences from '../GuidanceReferences';
import InstructionText from '../InstructionText';
import React from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Paper, TextField, Typography } from '@mui/material';
import { RaContactsProvider, RaStepContacts, raContactRoleLabels } from '../../ra-training/RaContacts';
import { createFeedbackSubmissionId, submitFeedback } from '../../api/stageFunctions';
import type { MissionPartnerGuidanceCard } from './types';
import { guidanceDisplayText } from './guidanceDisplay';

type Props = {
  card: MissionPartnerGuidanceCard;
  busy: boolean;
  onSelect?: (card: MissionPartnerGuidanceCard, selectionKey: string) => void;
};
type ContactFeedback = { roleId: string; step: MissionPartnerGuidanceCard['steps'][number]; submissionId: string };

function GuidanceContent({ card, busy, onSelect, scopeKey }: Props & { scopeKey: string }) {
  const [feedback, setFeedback] = React.useState<ContactFeedback | null>(null);
  const [details, setDetails] = React.useState('');
  const [status, setStatus] = React.useState<'idle' | 'sending' | 'error' | 'saved'>('idle');
  const alive = React.useRef(true);
  React.useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  const display = guidanceDisplayText();
  const bluf = display(/^BLUF:/i.test(card.bluf) ? card.bluf : `BLUF: ${card.bluf}`);
  const button = (option: MissionPartnerGuidanceCard['options'][number]) => <Button key={option.selection_key}
    size="small" variant="outlined" disabled={busy || !['ready', 'selected'].includes(card.state) || !onSelect}
    onClick={() => onSelect?.(card, option.selection_key)} sx={{ mt: 1, mr: 1, minHeight: 40 }}>{option.label}</Button>;
  const provideContact = (step: MissionPartnerGuidanceCard['steps'][number], roleId: string) => {
    setFeedback({ roleId, step, submissionId: createFeedbackSubmissionId() });
    setDetails(''); setStatus('idle');
  };
  const sendContact = async () => {
    if (!feedback || status === 'sending' || !details.trim()) return;
    setStatus('sending');
    const message = [
      'Provide Mission Partner contact',
      `Unit: ${card.contact_unit || 'Not provided'}`,
      `Role: ${raContactRoleLabels[feedback.roleId] || feedback.roleId} (${feedback.roleId})`,
      `Scenario: ${card.scenario_id || 'Not provided'}`,
      `Step: ${feedback.step.id}`,
      `Instruction: ${feedback.step.text}`,
      'Source: Home Chat / Mission Partner guidance',
      `Content version: ${card.content_version}`,
      `Contact details: ${details.trim()}`,
    ].join('\n');
    try {
      const result = await submitFeedback(message, 'Warfighter Training', `Contact for ${card.scenario_id || 'guidance'} / ${feedback.step.id}`, [], feedback.submissionId,
        { sourcePage: 'homepage', sourceArea: 'chat', sourceConfidence: 'high' }, feedback.step.text);
      if (!alive.current) return;
      if (result.success) { setFeedback(null); setDetails(''); setStatus('saved'); }
      else setStatus('error');
    } catch { if (alive.current) setStatus('error'); }
  };
  return <RaContactsProvider projectedData={card.contact_directory ?? null} scopeKey={scopeKey}>
    <Paper data-testid="mission-partner-guidance-card" data-role="assistant" elevation={0} aria-live="polite" aria-busy={busy}
      sx={{ alignSelf: 'flex-start', width: { xs: '100%', sm: 'auto' }, maxWidth: { xs: '100%', sm: '85%' }, px: 1.5, py: 1,
        bgcolor: 'surfaceMuted', color: 'text.primary', borderRadius: 2, boxShadow: 1, overflowWrap: 'anywhere' }}>
      <Typography component="h3" variant="subtitle1" fontWeight={700}>{bluf}</Typography>
      {card.clarification_question ? <Typography sx={{ mt: 1 }}>{display(card.clarification_question)}</Typography> : <>
        {card.steps.length > 0 && <Box component="ol" sx={{ pl: 3, mb: 0, '& li': { mb: 1 } }}>
          {card.steps.map(step => <li key={step.id}><Typography variant="body2"><InstructionText text={display(step.text)} /></Typography>
            <RaStepContacts roleIds={step.contact_role_ids || []} context={{ kind: 'scenario', id: card.scenario_id || undefined }}
              onProvideContact={card.contact_directory ? undefined : roleId => provideContact(step, roleId)} />
            {card.options.filter(option => option.step_id === step.id).map(button)}</li>)}
        </Box>}
        {card.options.filter(option => !option.step_id).map(button)}
        {card.references.length > 0 && <GuidanceReferences count={card.references.length}>
          {card.references.map(ref => <Link key={ref.url} href={ref.url} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ display: 'block' }}>{ref.label}</Link>)}
        </GuidanceReferences>}
      </>}
      {status === 'saved' && <Typography role="status" variant="body2" sx={{ mt: 1 }}>Contact submitted.</Typography>}
    </Paper>
    <Dialog open={Boolean(feedback)} onClose={() => status !== 'sending' && setFeedback(null)} fullWidth maxWidth="sm">
      <DialogTitle>Provide contact</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>Share contact details for {feedback ? raContactRoleLabels[feedback.roleId] || feedback.roleId : 'this role'}. This creates a feedback item for the Waypoint maintainers.</Typography>
        <TextField autoFocus fullWidth multiline minRows={2} value={details} disabled={status === 'sending'}
          inputProps={{ maxLength: 1000, 'aria-label': 'Contact details' }} onChange={event => setDetails(event.target.value)} />
        {status === 'error' && <Alert severity="error" sx={{ mt: 2 }}>Contact could not be submitted. Please retry.</Alert>}
      </DialogContent>
      <DialogActions><Button disabled={status === 'sending'} onClick={() => setFeedback(null)}>Cancel</Button>
        <Button variant="contained" disabled={status === 'sending' || !details.trim()} onClick={() => void sendContact()}>Send contact</Button></DialogActions>
    </Dialog>
  </RaContactsProvider>;
}

export default function MissionPartnerGuidanceCardView(props: Props) {
  const { card } = props;
  const scopeKey = JSON.stringify(['guidance', card.card_id, card.perspective, card.contact_unit ?? null, card.contact_directory?.unit_id ?? null, card.contact_directory?.directory_revision ?? null]);
  return <GuidanceContent key={scopeKey} {...props} scopeKey={scopeKey} />;
}
